import { DatePipe } from "@angular/common";
import { Component, computed, effect, inject, input, OnInit, output } from "@angular/core";
import { PeriodDefinitionType } from "@models/map/market-filter.model";
import { EvDialogModule } from "ev-dialog";
import { EvTooltipModule } from "ev-tooltip";
import { StatisticsLine } from "src/models/estate/IResidenceSaleInArea";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { MarketSalesStateService } from "src/services/estate/market-sales-state.service";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";

@Component({
    selector: "report-wizard",
    imports: [DatePipe, EvTooltipModule, EvDialogModule],
    templateUrl: "./report-wizard.component.html",
    styleUrl: "./report-wizard.component.scss",
})
export class ReportWizardComponent implements OnInit {
    showLoader = input<boolean>(false);
    showDialog = input<boolean>(false);
    statistics = input.required<StatisticsLine[]>();
    onClose = output<boolean>();
    onConfirm = output<any>();

    private readonly authorizationService = inject(EvAuthorizationService);
    private readonly mapStateService = inject(MarketSalesStateService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    statisticsReportImgUrl = "assets/images/statisticsreport.png";

    filterForm = this.mapStateService.filterForm;

    periodType = PeriodDefinitionType;

    hasDemographicsRole = this.authorizationService.hasPermissionSignal(EVServiceType.EvWeb_Demography, true, false);
    debugRole = this.authorizationService.hasPermissionSignal(EVServiceType.Internal_Debug, true, false);
    hasAllowedEstateTypeMix: boolean = true;
    hasMarketSharesRole = this.authorizationService.hasPermissionSignal(EVServiceType.EvWeb_AgentReport, true, false);
    areaComparisionToolTipEnabled = false;
    hasAreas: boolean = false;

    reportWizardCheckItems: wizardOptions = {
        prices: true,
        matrixes: true,
        demographyBuyers: false,
        demographyResidents: false,
        estateAgentMarketShares: false,
        areaComparison: false,
    };
    hasReportData: hasData = {
        areaInhabitantDemography: true,
        demography: true,
        marketShares: true,
        priceStatistics: true,
        demographyLimitReached: false,
        hasTooManyOrFewAreas: false,
    };

    constructor() {
        effect(() => {
            if (!this.mapStateService.isAllowedEstateTypeMix()) {
                this.hasAllowedEstateTypeMix = false;
                this.reportWizardCheckItems.demographyBuyers = false;
                this.reportWizardCheckItems.demographyResidents = false;
            } else {
                this.hasAllowedEstateTypeMix = true;
                this.reportWizardCheckItems.demographyBuyers = this.hasDemographicsRole();
                this.reportWizardCheckItems.demographyResidents = this.hasDemographicsRole();
            }
            if (this.statistics()) {
                const totalLine: StatisticsLine = this.statistics().find((y) => y.isTotal);
                if (totalLine) {
                    if (totalLine.numberOfEstates && totalLine.numberOfEstates > 0) {
                        this.hasReportData.priceStatistics = true;
                    }
                }
            }
            const mapData = this.areaBuilderService.getAreasAndPolygons();
            const totalLength = mapData.areas.length + mapData.selfDrawnPolygons.length;
            if (totalLength > 4 || totalLength < 2) {
                this.hasReportData.hasTooManyOrFewAreas = true;
                this.hasAreas = false;
                this.reportWizardCheckItems.areaComparison = false;
            } else if (totalLength >= 2) {
                this.hasReportData.hasTooManyOrFewAreas = false;
                this.hasAreas = true;
                this.reportWizardCheckItems.areaComparison = true;
            }
        });
    }

    ngOnInit(): void {
        if (this.hasDemographicsRole()) {
            this.reportWizardCheckItems.demographyBuyers = true;
            this.reportWizardCheckItems.demographyResidents = true;
        }
        if (this.hasMarketSharesRole()) {
            this.reportWizardCheckItems.estateAgentMarketShares = true;
        }

        this.hasReportData.priceStatistics = false;
    }

    computedTimePeriod = computed(() => {
        let from = null;
        let to = null;
        if (this.filterForm.timePeriod.useFromToDate().value()) {
            from = this.filterForm.timePeriod.fromDate().value();
            to = this.filterForm.timePeriod.toDate().value();
        } else {
            from = new Date();
            from.setHours(0, 0, 0, 0);
            from = new Date(from.setMonth(from.getMonth() - this.filterForm.timePeriod.numberOfMonthsToShow().value()));
            to = new Date();
        }
        return { fromDate: from, toDate: to };
    });

    confirmReport(generatePdf = true) {
        this.onConfirm.emit({ options: this.reportWizardCheckItems, generatePdf });
    }
}

export interface wizardOptions {
    prices: boolean;
    demographyBuyers: boolean;
    demographyResidents: boolean;
    estateAgentMarketShares: boolean;
    areaComparison: boolean;
    matrixes: boolean;
}

export interface hasData {
    priceStatistics: boolean;
    areaInhabitantDemography: boolean;
    demography: boolean;
    marketShares: boolean;
    demographyLimitReached: boolean;
    hasTooManyOrFewAreas: boolean;
}
